import { supabase } from "@/integrations/supabase/client";
import type { Catchment, CatchmentSchool, SearchResult } from "./types";

interface RawCatchment {
  id: string;
  code: string | null;
  name: string;
  description: string | null;
  district: string | null;
  region: string | null;
  county: string | null;
  subcounty: string | null;
  parish: string | null;
  catchment_type: string;
  geometry: any;
  area_km2: number | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface RawSchool {
  id: string;
  name: string;
  registration_no: string | null;
  latitude: number | null;
  longitude: number | null;
  district: string | null;
  region: string | null;
  county: string | null;
  subcounty: string | null;
  parish: string | null;
  school_type: string | null;
  ownership: string | null;
  boarding_type: string | null;
  education_level: string | null;
  location_status: string | null;
  student_count: number;
  male_student_count: number;
  female_student_count: number;
  staff_count: number;
  capacity: number | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
  active: boolean;
}

interface RawCatchmentSchool {
  catchment_id: string;
  relationship_type: string;
  priority: number;
  schools: RawSchool | RawSchool[];
}

export async function fetchCatchments(): Promise<Catchment[]> {
  const { data, error } = await supabase
    .from("catchments")
    .select("*")
    .order("name");
  if (error) throw error;
  if (!data) return [];

  const { data: csData } = await supabase
    .from("catchment_schools")
    .select("catchment_id, school_id:schools(id, student_count)");

  const schoolCounts = new Map<string, { count: number; students: number }>();
  for (const row of (csData as any[]) ?? []) {
    const cid = row.catchment_id;
    if (!schoolCounts.has(cid)) schoolCounts.set(cid, { count: 0, students: 0 });
    const entry = schoolCounts.get(cid)!;
    entry.count += 1;
    entry.students += row.schools?.student_count ?? 0;
  }

  return (data as RawCatchment[]).map((c) => {
    const sc = schoolCounts.get(c.id);
    return {
      id: c.id,
      code: c.code,
      name: c.name,
      description: c.description,
      country: "Uganda",
      region: c.region,
      district: c.district,
      county: c.county,
      subcounty: c.subcounty,
      parish: c.parish,
      catchment_type: (c.catchment_type as Catchment["catchment_type"]) ?? "district",
      geometry: c.geometry as any,
      area_km2: c.area_km2,
      status: (c.status as Catchment["status"]) ?? "active",
      school_count: sc?.count ?? 0,
      student_count: sc?.students ?? 0,
      created_at: c.created_at,
      updated_at: c.updated_at,
    };
  });
}

function mapRawSchool(s: RawSchool, catchmentId?: string, relType?: string): CatchmentSchool {
  return {
    id: s.id,
    name: s.name,
    code: s.registration_no,
    latitude: s.latitude,
    longitude: s.longitude,
    region: s.region,
    district: s.district,
    county: s.county,
    subcounty: s.subcounty,
    parish: s.parish,
    education_level: (s.education_level as CatchmentSchool["education_level"]) ?? null,
    ownership: (s.ownership as CatchmentSchool["ownership"]) ?? null,
    boarding_type: (s.boarding_type as CatchmentSchool["boarding_type"]) ?? null,
    student_count: s.student_count ?? 0,
    male_student_count: s.male_student_count ?? 0,
    female_student_count: s.female_student_count ?? 0,
    teacher_count: s.staff_count ?? 0,
    capacity: s.capacity,
    phone: s.phone,
    email: s.email,
    website: s.website,
    address: s.address,
    status: s.active ? "active" : "inactive",
    location_status: (s.location_status as CatchmentSchool["location_status"]) ?? null,
    catchment_id: catchmentId ?? null,
    relationship_type: (relType as CatchmentSchool["relationship_type"]) ?? null,
  };
}

export async function fetchSchools(): Promise<CatchmentSchool[]> {
  const { data: csData, error } = await supabase
    .from("catchment_schools")
    .select(`
      catchment_id,
      relationship_type,
      priority,
      schools!inner (
        id, name, registration_no, latitude, longitude,
        district, region, county, subcounty, parish,
        school_type, ownership, boarding_type, education_level, location_status,
        student_count, male_student_count, female_student_count,
        staff_count, capacity, phone, email, website, address, active
      )
    `)
    .order("priority", { ascending: true });

  if (error) throw error;
  if (!csData) return [];

  const seen = new Set<string>();
  const schools: CatchmentSchool[] = [];
  for (const row of (csData as RawCatchmentSchool[]) ?? []) {
    const s = Array.isArray(row.schools) ? row.schools[0] : row.schools;
    if (!s || seen.has(s.id)) continue;
    seen.add(s.id);
    schools.push(mapRawSchool(s, row.catchment_id, row.relationship_type));
  }

  const { data: allSchools } = await supabase
    .from("schools")
    .select("*")
    .order("name");

  for (const s of (allSchools as RawSchool[]) ?? []) {
    if (seen.has(s.id)) continue;
    seen.add(s.id);
    schools.push(mapRawSchool(s));
  }

  return schools;
}

export function searchAll(
  catchments: Catchment[],
  schools: CatchmentSchool[],
  query: string
): SearchResult[] {
  const q = query.toLowerCase().trim();
  if (!q) return [];
  const results: SearchResult[] = [];

  for (const c of catchments) {
    if (c.name.toLowerCase().includes(q) || c.district?.toLowerCase().includes(q)) {
      results.push({
        type: "catchment",
        id: c.id,
        label: c.name,
        sublabel: `${c.school_count} school${c.school_count === 1 ? "" : "s"} · ${c.district ?? c.region ?? "Uganda"}`,
        lat: null,
        lng: null,
        catchmentId: c.id,
        schoolCount: c.school_count,
      });
    }
  }

  for (const s of schools) {
    if (
      s.name.toLowerCase().includes(q) ||
      s.district?.toLowerCase().includes(q) ||
      s.code?.toLowerCase().includes(q)
    ) {
      results.push({
        type: "school",
        id: s.id,
        label: s.name,
        sublabel: s.district ?? s.region ?? "Uganda",
        lat: s.latitude,
        lng: s.longitude,
        catchmentId: s.catchment_id ?? undefined,
      });
    }
  }

  const seenDistricts = new Set<string>();
  for (const s of schools) {
    if (s.district && s.district.toLowerCase().includes(q) && !seenDistricts.has(s.district)) {
      seenDistricts.add(s.district);
      results.push({
        type: "district",
        id: s.district,
        label: s.district,
        sublabel: s.region ?? "Uganda",
        lat: s.latitude,
        lng: s.longitude,
      });
    }
  }

  return results.slice(0, 20);
}
